import { useMemo } from 'react';
import { matchPermissions } from '../../../utils/Permission';

/**
 * Custom hook for managing table permissions
 */
export const useTablePermissions = (resource, userDetails, customPermissions = null) => {
  const permissions = useMemo(() => {
    if (customPermissions) {
      return customPermissions;
    }

    if (!userDetails || !resource) {
      return {
        canView: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canExport: false,
      };
    }

    const matchPermissionsForUser = matchPermissions(userDetails);

    return {
      canView: matchPermissionsForUser(`${resource}.view`),
      canCreate: matchPermissionsForUser(`${resource}.create`),
      canUpdate: matchPermissionsForUser(`${resource}.update`),
      canDelete: matchPermissionsForUser(`${resource}.delete`),
      canExport: matchPermissionsForUser(`${resource}.export`),
    };
  }, [resource, userDetails, customPermissions]);

  /**
   * Check if user has specific permission
   */
  const hasPermission = permission => {
    if (customPermissions) {
      return customPermissions[permission] || false;
    }

    if (!userDetails) return false;

    const matchPermissionsForUser = matchPermissions(userDetails);
    return matchPermissionsForUser(permission);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = permissionList => {
    return permissionList.some(permission => hasPermission(permission));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = permissionList => {
    return permissionList.every(permission => hasPermission(permission));
  };

  /**
   * Filter columns based on permissions
   */
  const filterColumnsByPermission = columns => {
    return columns.filter(col => {
      if (!col.permission) return true;
      return hasPermission(col.permission);
    });
  };

  /**
   * Filter actions based on permissions
   */
  const filterActionsByPermission = actions => {
    return actions.filter(action => {
      if (!action.permission) return true;
      return hasPermission(action.permission);
    });
  };

  return {
    ...permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    filterColumnsByPermission,
    filterActionsByPermission,
  };
};
